import { HeatmapRangeEditor } from "features/shared/components/heatmap/HeatmapRangeEditor";
import { HeatmapRange } from "features/shared/components/heatmap/types";
import { formatTo2DigitString } from "features/shared/lib/formatters";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { ScheduleGrid } from "../model/types";

import { scheduleGridToValues, scheduleValuesToGrid } from "../lib/scheduleGrid";
import { DAY_KEYS, HOUR_KEYS } from "../model/types";

type ScheduleHeatmapEditorProps = {
  grid: ScheduleGrid;
  onGridChange: (grid: ScheduleGrid) => void;
  range: HeatmapRange;
  readOnly?: boolean;
};

export const ScheduleHeatmapEditor = ({ grid, onGridChange, range, readOnly }: ScheduleHeatmapEditorProps) => {
  const { t } = useTranslation();
  const values = useMemo(() => scheduleGridToValues(grid), [grid]);
  const columnLabels = useMemo(() => DAY_KEYS.map((day) => t(`Execution.schedules.days.${day}`)), [t]);
  const rowLabels = useMemo(() => HOUR_KEYS.map((hour) => formatTo2DigitString(hour)), []);
  const handleValuesChange = useCallback(
    (nextValues: (null | number)[][]) => onGridChange(scheduleValuesToGrid(nextValues)),
    [onGridChange]
  );

  return (
    <HeatmapRangeEditor
      columnLabels={columnLabels}
      labels={{
        applyLabel: t("Execution.schedules.modal.apply"),
        cancelLabel: t("general.cancel"),
        getModalDescription: (count) => t("Execution.schedules.modal.description", { count }),
        modalTitle: t("Execution.schedules.modal.title"),
        valueLabel: t("Execution.schedules.modal.valueLabel"),
      }}
      onValuesChange={handleValuesChange}
      range={range}
      readOnly={readOnly}
      rowHeaderLabel={t("Execution.schedules.table.hour")}
      rowLabels={rowLabels}
      values={values}
    />
  );
};
